import { Injectable } from '@angular/core';
import { DayOfWeek } from '../models/day-of-week';
import { TimeRestriction } from '../models/time-restriction';
import { BookingSlot } from '../models/booking-slot';
import { AreaItem } from '../models/area-item';


@Injectable()
export class TimeRestrictionService {

  private _allowedDaysOfWeek = new Map<DayOfWeek, Map<number, Array<TimeRestriction>>>();
  private _areaItemRestrictions = new Set<number>();

  initialize(timeRestrictions: TimeRestriction[]): void {
    if (!timeRestrictions) {
      return;
    }

    timeRestrictions.forEach(timeRestriction => {
      this._addTimeRestriction(timeRestriction);
    });

    this._sortTimeRestriction();
  }

  private _addTimeRestriction(timeRestriction: TimeRestriction): void {
    let dayRestrictions = this._allowedDaysOfWeek.get(timeRestriction.dayOfWeek);
    if (!dayRestrictions) {
      dayRestrictions = new Map<number, Array<TimeRestriction>>();
      this._allowedDaysOfWeek.set(timeRestriction.dayOfWeek, dayRestrictions);
    }

    let restrictions = dayRestrictions.get(timeRestriction.areaItemId);
    if (!restrictions) {
      restrictions = new Array<TimeRestriction>();
      dayRestrictions.set(timeRestriction.areaItemId, restrictions);
    }

    restrictions.push(timeRestriction);

    this._areaItemRestrictions.add(timeRestriction.areaItemId);
  }

  private _sortTimeRestriction(): void {
    this._allowedDaysOfWeek.forEach(dayRestrictions => {
      dayRestrictions.forEach(restriction => {
        restriction.sort((a, b) => b.fromTime - a.fromTime);
      });
    });
  }

  clear(): void {
    this._allowedDaysOfWeek.clear();
    this._areaItemRestrictions.clear();
  }

  get daysOfWeek(): Array<number> {
    if (!this._allowedDaysOfWeek.size) {
      return Array<number>();
    }

    return Array.from(this._allowedDaysOfWeek.keys());
  }

  filterSelectableBookingSlots(bookingSlots: BookingSlot[], day: DayOfWeek, areaItems: AreaItem[]): BookingSlot[] {
    if (!bookingSlots || !this._allowedDaysOfWeek.size || !areaItems) {
      return bookingSlots;
    }

    if (!this._allowedDaysOfWeek.has(day)) {
      return [];
    }

    const dayTimeRestrictions = this._allowedDaysOfWeek.get(day);
    if (!dayTimeRestrictions || !dayTimeRestrictions.size) {
      return [];
    }
    return bookingSlots.filter(bookingSlot => this._canSelectBooking(bookingSlot, dayTimeRestrictions, areaItems));
  }

  private _canSelectBooking(bookingSlot: BookingSlot, areaItemToTimeRestrictions: Map<number, Array<TimeRestriction>>, areaItems: AreaItem[]): boolean {
    if (!bookingSlot || !areaItems) {
      return false;
    }

    if (!areaItemToTimeRestrictions || !areaItemToTimeRestrictions.size) {
      return true;
    }

    const foundAreaItemsIds = new Array<number>();

    for (const areaItem of areaItems) {
      if (!areaItem) {
        continue;
      }

      const timeRestrictions = areaItemToTimeRestrictions.get(areaItem.id);
      if (!timeRestrictions) {
        continue;
      }

      if (this._canSelectBookingSlotByTime(bookingSlot, areaItem.minutes, timeRestrictions)) {
        foundAreaItemsIds.push(areaItem.id);
        continue;
      }
    }

    bookingSlot.areaItemsIds = foundAreaItemsIds;

    return foundAreaItemsIds.length !== 0;
  }

  isAvailableAreaItemByDateAndSlot(areaItem: AreaItem, date: Date, bookingSlot: BookingSlot): boolean {
    if (!areaItem || !bookingSlot || !date) {
      return false;
    }

    const areaItemToTimeRestrictions = this._allowedDaysOfWeek.get(date.getDay());
    if (!areaItemToTimeRestrictions || !areaItemToTimeRestrictions.size) {
      return false;
    }

    const timeRestrictions = areaItemToTimeRestrictions.get(areaItem.id);
    if (!timeRestrictions) {
      return false;
    }

    const areaItemTimeLength = areaItem.minutes / 60;

    for (const timeRestriction of timeRestrictions) {
      if (
        timeRestriction.fromTime <= bookingSlot.fromTime
        && timeRestriction.toTime >= bookingSlot.fromTime + areaItemTimeLength
        && timeRestriction.toTime >= bookingSlot.toTime
        && timeRestriction.fromDate <= date
        && (!timeRestriction.toDate || timeRestriction.toDate >= date)
      ) {
        return true;
      }
    }
    
    return false;
  }

  private _canSelectBookingSlotByTime(bookingSlot: BookingSlot, areaToMinutes: number, timeRestrictions: TimeRestriction[]): boolean {
    if (!areaToMinutes || !timeRestrictions) {
      return false;
    }

    const areaItemTimeLength = areaToMinutes / 60;

    for (const timeRestriction of timeRestrictions) {
      if (
        timeRestriction.fromTime <= bookingSlot.fromTime
        && timeRestriction.toTime >= bookingSlot.fromTime + areaItemTimeLength
        && timeRestriction.toTime >= bookingSlot.toTime
      ) {
        return true;
      }
    }

    return false;
  }

  isDateValid(date: Date): boolean {
    if (!date) {
      return false;
    }

    const areaItemToRestrictions = this._allowedDaysOfWeek.get(date.getDay());
    if (!areaItemToRestrictions || !areaItemToRestrictions.size) {
      return false;
    }

    for (const [, value] of Array.from(areaItemToRestrictions.entries())) {
      if (value && value.some(item => item.isDateValid(date))) {
        return true;
      }
    }

    return false;
  }
}
